// "What came in" (F9b B5): after a run, the production's folders as they were in Higgsfield, with thumbnails, and
// the elements the import brought (workspace-wide), with handle, kind and reference still. "Liked in Higgsfield"
// filters the stills (a filter only: the crew picks the picks). Possible duplicates (a handle the import had to
// suffix) are marked. Reads only; the folder action "Make shots from this folder" lives on the folder itself.
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { externsVersion, knownExtern, onExternChange } from "../../data/imports-api.ts";
import { DEPARTMENT_LABEL, elementDepartment } from "../../departments.ts";
import type { Folder } from "../../folders.ts";
import { formatRoute } from "../../route.ts";
import type { Element } from "../../types.ts";
import { useApp, useFolders } from "../app.ts";
import { Icon } from "../icons.tsx";
import { DeptDot, Skeleton, Still } from "../kit.tsx";

const THUMBS = 8;

/** Re-render when what is known about imported assets changes (the folder listing fills it). */
const useExternVersion = (): number => useSyncExternalStore(onExternChange, externsVersion, () => 0);

export function WhatCameIn({ projectId, since }: { projectId: string; since: string }) {
  const { folders: store, go } = useApp();
  const { folders, load } = useFolders(store, projectId);
  useExternVersion();
  const [liked, setLiked] = useState(false);
  const assets = folders.flatMap((f) => f.items.filter((i) => i.kind === "asset"));
  const likedCount = assets.filter((a) => knownExtern(a.id)?.liked).length;
  const knowsLikes = assets.some((a) => knownExtern(a.id));
  const children = useMemo(() => {
    const m = new Map<string | null, Folder[]>();
    for (const f of folders) m.set(f.parentId, [...(m.get(f.parentId) ?? []), f]);
    for (const l of m.values()) l.sort((a, b) => a.position - b.position);
    return m;
  }, [folders]);
  const roots = children.get(null) ?? [];
  return (
    <div className="imp-came">
      <section className="imp-came-sec" aria-label="Folders">
        <div className="imp-came-head">
          <h4>Folders</h4>
          <span className="k-meta">
            {folders.length} {folders.length === 1 ? "folder" : "folders"} ·{" "}
            {assets.length.toLocaleString("en")} files
          </span>
          <button
            type="button"
            className="k-chip imp-came-filter"
            aria-pressed={liked}
            disabled={!knowsLikes}
            title={knowsLikes ? undefined : "This server does not say yet what was liked in Higgsfield."}
            onClick={() => setLiked((v) => !v)}
          >
            <Icon name="check" size={10} />
            <span className="k-chip-label">Liked in Higgsfield</span>
            <span className="k-chip-count">{likedCount}</span>
          </button>
        </div>
        {load.status === "loading" && folders.length === 0 ? (
          <Skeleton h={96} />
        ) : load.status === "error" ? (
          <p className="imp-check conflict">The folders could not be loaded: {load.error}</p>
        ) : roots.length === 0 ? (
          <p className="imp-empty-group">No folders yet. They show here as soon as the first ones are in.</p>
        ) : (
          <ul className="imp-tree" aria-label="Folders">
            {roots.map((f) => (
              <FolderNode
                key={f.id}
                folder={f}
                tree={children}
                liked={liked}
                depth={0}
                onOpen={(id) => go({ view: "project", projectId, loc: { kind: "folder", id } })}
              />
            ))}
          </ul>
        )}
      </section>
      <ImportedElements since={since} />
    </div>
  );
}

function FolderNode({
  folder,
  tree: children,
  liked,
  depth,
  onOpen,
}: {
  folder: Folder;
  tree: Map<string | null, Folder[]>;
  liked: boolean;
  depth: number;
  onOpen: (id: string) => void;
}) {
  const items = folder.items.filter((i) => i.kind === "asset" && (!liked || knownExtern(i.id)?.liked));
  const kids = children.get(folder.id) ?? [];
  if (liked && items.length === 0 && kids.length === 0) return null;
  return (
    <li className="imp-node" style={{ "--depth": depth } as CSSProperties}>
      <button type="button" className="imp-node-head" onClick={() => onOpen(folder.id)}>
        <Icon name="folder" size={14} />
        <span className="imp-node-name">{folder.name}</span>
        <span className="k-meta">{items.length}</span>
      </button>
      {items.length > 0 && (
        <span className="imp-thumbs">
          {items.slice(0, THUMBS).map((i) => (
            <Still key={i.id} src={i.id} alt="" decorative className="imp-thumbs-still" />
          ))}
          {items.length > THUMBS && <span className="imp-thumbs-more k-meta">+{items.length - THUMBS}</span>}
        </span>
      )}
      {kids.length > 0 && (
        <ul>
          {kids.map((k) => (
            <FolderNode
              key={k.id}
              folder={k}
              tree={children}
              liked={liked}
              depth={depth + 1}
              onOpen={onOpen}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

type Imported = Element & { created_at?: string; ref?: string | null };

/** Elements of the whole workspace made by this run or after it (the import makes them workspace-wide). */
function ImportedElements({ since }: { since: string }) {
  const { api, ws } = useApp();
  const [list, setList] = useState<Imported[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let live = true;
    const from = Date.parse(since) - 60_000;
    api.listElements(ws).then(
      async (all) => {
        const mine = (all as Imported[]).filter(
          (e) => !e.project_id && !e.archived_at && (!e.created_at || Date.parse(e.created_at) >= from),
        );
        const withRefs = await Promise.all(
          mine.slice(0, 40).map(async (e) => {
            const refs = await api.listReferences(ws, e.id, e.current_version ?? 1).catch(() => []);
            const ref = refs.find((r) => r.canonical) ?? refs[0];
            return { ...e, ref: ref?.asset_version_id ?? null };
          }),
        );
        if (live) setList(withRefs);
      },
      () => live && setError(true),
    );
    return () => {
      live = false;
    };
  }, [api, ws, since]);
  const handles = new Set((list ?? []).map((e) => e.handle));
  let body: ReactNode;
  if (error) body = <p className="imp-check conflict">The elements could not be loaded.</p>;
  else if (!list) body = <Skeleton h={72} />;
  else if (list.length === 0)
    body = <p className="imp-empty-group">No new elements. The ones you had are used as they are.</p>;
  else
    body = (
      <ul className="imp-els">
        {list.map((e) => {
          const dept = elementDepartment(e.subtype);
          const base = e.handle.replace(/-\d+$/, "");
          const dup = base !== e.handle && handles.has(base);
          return (
            <li key={e.id}>
              <a className="imp-el" href={formatRoute({ view: "workspace-element", elementId: e.id })}>
                <Still src={e.ref ?? null} alt="" decorative className="imp-el-still" />
                <span className="imp-el-text">
                  <span className="imp-el-name">{e.name}</span>
                  <span className="k-meta">@{e.handle}</span>
                  <span className="imp-el-kind">
                    {dept && <DeptDot dept={dept} />}
                    {kindLabel(e.subtype)}
                    {dept && <span className="sr-only"> ({DEPARTMENT_LABEL[dept]})</span>}
                  </span>
                  {dup && <span className="imp-check note">Maybe the same as @{base}</span>}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    );
  return (
    <section className="imp-came-sec" aria-label="Elements">
      <div className="imp-came-head">
        <h4>Elements</h4>
        {list && <span className="k-meta">{list.length} new · for every production in the workspace</span>}
      </div>
      {body}
    </section>
  );
}

const kindLabel = (s: string): string =>
  s === "character" ? "Character" : s === "location" ? "Location" : s === "prop" ? "Prop" : "Style";
